import React from 'react';
import Menu from '../Menu/Menu';
import { FilterProps } from './Filters';
import Styled from './ThresholdFilter.styles';
import { HudIcon, IconType } from '../Icons/HudIcons';
import {
  useFocusTrap,
  useMergedRef,
  useDebouncedCallback,
} from '@mantine/hooks';
import { useEffect, useRef } from 'react';
import { InfoPopover } from '../HoverPopover/InfoPopover';

export const Operators = {
  gte: 'gte',
  lte: 'lte',
} as const;

export interface ThresholdFilterProps<Nullable extends boolean = false>
  extends Omit<FilterProps, 'onClear'> {
  icon?: IconType;
  tooltip?: string;
  value: Nullable extends true ? number | null : number;
  unit?: string;
  operator: (typeof Operators)[keyof typeof Operators];
  presetValues: number[];
  readonly?: boolean;
  readonlyOperator?: boolean;
  nullable?: Nullable;
  onOperatorChange: (
    operator: (typeof Operators)[keyof typeof Operators],
  ) => void;
  onValueChange: (
    value: Nullable extends true ? number | null : number,
  ) => void;
  onClear?: () => void;
  role?: string;
  debounce?: boolean; // todo: should debounce happen here or in the page component?

  options?: {
    closePopoverOnPresetChange: boolean;
  };

  minSize?: number;
  maxLength?: number;
}

export const ThresholdFilter = <Nullable extends boolean = false>({
  icon,
  label,
  tooltip,
  value,
  unit,
  operator,
  presetValues,
  readonly,
  readonlyOperator = false,
  nullable,
  onClear,
  onOperatorChange,
  onValueChange,
  options,
  role,
  minSize = 1,
  maxLength,
  debounce = true,
  ...rest
}: ThresholdFilterProps<Nullable>) => {
  const focusTrapRef = useFocusTrap();
  const inputRef = useRef<HTMLInputElement>(null);
  const mergedRef = useMergedRef(focusTrapRef, inputRef);
  const [inputValue, setInputValue] = React.useState(
    value !== null ? value.toString() : '',
  );
  // const [debouncedValue] = useDebouncedValue(inputValue, debounce ? 400 : 0);
  const closable = onClear !== undefined;

  const updateValue = useDebouncedCallback(
    (newValue: number | null) => {
      if (nullable) {
        (onValueChange as (value: number | null) => void)(newValue);
      } else if (newValue !== null) {
        (onValueChange as (value: number) => void)(newValue);
      } else {
        (onValueChange as (value: number) => void)(0);
      }
    },
    debounce ? 400 : 0,
  );

  // Call the debounced onChange callback
  useEffect(() => {
    if (inputValue === '') {
      if (nullable && value !== null) {
        updateValue(null);
      } else if (!nullable && value !== 0) {
        updateValue(0);
      }
    } else {
      const numberedValue = Number(inputValue);
      if (numberedValue !== value) {
        updateValue(numberedValue);
      }
    }
  }, [inputValue]);

  useEffect(() => {
    setInputValue(value !== null ? value.toString() : '');
  }, [value]);

  const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedInputValue = sanitizeInputValue(e.target.value);
    setInputValue(sanitizedInputValue);
  };

  const handleInputMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handlePresetClick = (presetValue: number) => {
    setInputValue(presetValue.toString());
  };

  const handleContainerClick = (_e: React.MouseEvent) => {
    if (!readonly && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (nullable) {
      updateValue(null);
    } else {
      updateValue(0);
    }
  };

  return (
    <Styled.Container {...rest} role={role} onClick={handleContainerClick}>
      <Styled.LabelContainer aria-label="title">
        {icon && <HudIcon icon={icon} size="18px" />}
        <Styled.Label>{label}</Styled.Label>
        {tooltip && <InfoPopover body={tooltip} />}
      </Styled.LabelContainer>
      <Styled.Content>
        <Styled.OperatorMenu disabled={readonly || readonlyOperator}>
          <Menu.Target>
            <Styled.OperatorButton disabled={readonly || readonlyOperator}>
              <HudIcon
                icon={operator === 'gte' ? 'comparison.gte' : 'comparison.lte'}
                size="20px"
              />
            </Styled.OperatorButton>
          </Menu.Target>
          <Menu.Dropdown>
            <Styled.OperatorItem
              onClick={() => onOperatorChange(Operators.gte)}
              selected={operator === Operators.gte}
              disabled={readonlyOperator}
            >
              <HudIcon icon="comparison.gte" size="20px" />
            </Styled.OperatorItem>
            <Styled.OperatorItem
              onClick={() => onOperatorChange(Operators.lte)}
              selected={operator === Operators.lte}
              disabled={readonlyOperator}
            >
              <HudIcon icon="comparison.lte" size="20px" />
            </Styled.OperatorItem>
          </Menu.Dropdown>
        </Styled.OperatorMenu>
        <Styled.PresetMenu
          closeOnItemClick={!!options?.closePopoverOnPresetChange}
          trapFocus={false}
          disabled={readonly || presetValues.length === 0}
        >
          <Menu.Target>
            <Styled.PresetTrigger disabled={readonly}>
              <Styled.InputContainer data-value={inputValue}>
                <Styled.ValueInput
                  size={Math.max(inputValue.length, minSize)}
                  aria-label="value"
                  aria-readonly={readonly}
                  inputMode="decimal"
                  pattern="[0-9]*[.]?[0-9]*"
                  ref={mergedRef}
                  value={inputValue}
                  onChange={handleInputValueChange}
                  onMouseDown={handleInputMouseDown}
                  readOnly={readonly}
                  maxLength={maxLength}
                />
                {unit && <Styled.Unit>{unit}</Styled.Unit>}
              </Styled.InputContainer>
            </Styled.PresetTrigger>
          </Menu.Target>
          <Menu.Dropdown>
            {presetValues.map((presetValue) => (
              <Styled.PresetItem
                key={presetValue}
                onClick={() => handlePresetClick(presetValue)}
                selected={value === presetValue}
              >
                {presetValue} {unit}
              </Styled.PresetItem>
            ))}
          </Menu.Dropdown>
        </Styled.PresetMenu>
      </Styled.Content>
      {!readonly && closable && (
        <Styled.ClearButton onClick={handleClear}>
          <HudIcon icon="other.close" size="20px" />
        </Styled.ClearButton>
      )}
    </Styled.Container>
  );
};

function sanitizeInputValue(value: string) {
  // Remove non-numeric characters
  const sanitizedValue = value.replace(/[^0-9.]/g, '');
  // Ensure only one decimal point exists
  const parts = sanitizedValue.split('.');
  return parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
}
